import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, CalendarCheck2 } from "lucide-react";
import { NAV } from "@/constants/testIds";
import { CONTACT } from "@/data/siteData";
import { trackBookingClick } from "@/lib/analytics";

const links = [
  { to: "/#prestazioni", label: "Prestazioni", testid: NAV.linkPrestazioni },
  { to: "/#onorari", label: "Onorari", testid: "nav-link-onorari" },
  { to: "/nutrizione", label: "Nutrizione", testid: "nav-link-nutrizione", badge: "Novità" },
  { to: "/#patologie", label: "Patologie", testid: NAV.linkPatologie },
  { to: "/#chi-sono", label: "Chi sono", testid: NAV.linkChiSono },
  { to: "/blog", label: "Blog", testid: NAV.linkBlog },
  { to: "/#dove-ricevo", label: "Studi", testid: NAV.linkContatti },
  { to: "/#contatti", label: "Contatti", testid: "nav-link-contatti-form" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location]);

  const handleAnchor = (to) => {
    if (!to.startsWith("/#")) return;
    const hash = to.replace("/", "");
    if (location.pathname === "/") {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <header
      className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "nav-blur" : "bg-transparent"
      }`}
      style={{ top: "var(--announce-h, 0px)" }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between gap-6">
        <Link
          to="/"
          data-testid={NAV.logo}
          className="flex items-baseline gap-2 group flex-shrink-0"
        >
          <span className="font-serif text-[22px] tracking-tight text-[#0B4A5A] whitespace-nowrap">
            Dr. Cristian Santus
          </span>
          <span
            className="hidden xl:inline text-[11px] uppercase tracking-[0.24em] text-[#C6A86B]"
            aria-hidden
          >
            · Gastroenterologo
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-6 xl:gap-8">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              onClick={() => handleAnchor(l.to)}
              className="relative text-[14px] text-gray-700 hover:text-[#0B4A5A] link-underline transition-colors whitespace-nowrap"
            >
              {l.label}
              {l.badge && (
                <span className="ml-1.5 inline-block align-top -mt-0.5 bg-[#C6A86B] text-white text-[8.5px] font-semibold tracking-[0.14em] uppercase px-1.5 py-0.5 rounded-full leading-none">
                  {l.badge}
                </span>
              )}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3 flex-shrink-0">
          <a
            data-testid={NAV.ctaBook}
            href={CONTACT.miodottore}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackBookingClick("navbar")}
            className="btn-primary text-[14px] py-3 px-6 whitespace-nowrap"
          >
            <CalendarCheck2 size={16} strokeWidth={1.6} />
            Prenota una visita
          </a>
        </div>

        <button
          type="button"
          aria-label="Menu"
          data-testid={NAV.mobileToggle}
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-[#0B4A5A] p-2"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile sheet */}
      {open && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={`${l.testid}-mobile`}
                onClick={() => handleAnchor(l.to)}
                className="text-[15px] text-gray-800"
              >
                {l.label}
                {l.badge && (
                  <span className="ml-2 bg-[#C6A86B] text-white text-[9px] font-semibold tracking-[0.14em] uppercase px-1.5 py-0.5 rounded-full">
                    {l.badge}
                  </span>
                )}
              </Link>
            ))}
            <a
              href={CONTACT.miodottore}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackBookingClick("navbar_mobile")}
              className="btn-primary mt-2 justify-center"
            >
              <CalendarCheck2 size={16} strokeWidth={1.6} />
              Prenota una visita
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
